import { useEffect, useMemo, useState } from "react";
import { Calculator, Check, X } from "lucide-react";
import { Button, SecondaryButton } from "@contractor-quoting/shared-ui";
import type { QuoteFormField, QuoteFormModel } from "../structured-intake-model.js";

export interface QuoteFormSubmission {
  projectType: string;
  field: string;
  value: string | number | boolean;
  unit?: string;
}

interface Props {
  busy: boolean;
  error: string;
  form: QuoteFormModel | undefined;
  onClose: () => void;
  onSubmit: (facts: QuoteFormSubmission[]) => Promise<void>;
  open: boolean;
}

function key(projectType: string, field: string) {
  return `${projectType}:${field}`;
}

function initialValue(field: QuoteFormField): string {
  if (field.value === null || field.value === undefined) return "";
  if (typeof field.value === "boolean") return field.value ? "true" : "false";
  return String(field.value);
}

function shown(field: QuoteFormField) {
  return field.valueType !== "photo" && (field.required || initialValue(field) !== "");
}

function inputValue(field: QuoteFormField, value: string): string | number | boolean {
  if (field.valueType === "boolean") return value === "true";
  if (["count", "number", "measurement"].includes(field.valueType)) return Number(value);
  return value.trim();
}

function QuoteFieldInput({
  field,
  id,
  onChange,
  value,
}: {
  field: QuoteFormField;
  id: string;
  onChange: (value: string) => void;
  value: string;
}) {
  const className =
    "mt-1 min-h-11 w-full rounded-xl border border-stone-300 bg-white px-3 text-stone-900 outline-none focus:border-emerald-700 focus:ring-4 focus:ring-emerald-100";
  if (field.valueType === "boolean") {
    return (
      <select
        aria-required={field.required}
        className={className}
        id={id}
        onChange={(event) => onChange(event.target.value)}
        value={value}
      >
        <option value="">Choose…</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  }
  if (field.valueType === "enum" && field.allowedValues.length > 0) {
    return (
      <select
        aria-required={field.required}
        className={className}
        id={id}
        onChange={(event) => onChange(event.target.value)}
        value={value}
      >
        <option value="">Choose…</option>
        {field.allowedValues.map((option) => (
          <option key={option} value={option.toLowerCase()}>
            {option.replaceAll("_", " ")}
          </option>
        ))}
      </select>
    );
  }
  return (
    <div className="relative">
      <input
        aria-required={field.required}
        className={`${className} ${field.unit ? "pr-20" : ""}`}
        id={id}
        min={field.valueType === "count" ? 0 : undefined}
        onChange={(event) => onChange(event.target.value)}
        step={["count", "number"].includes(field.valueType) ? 1 : "any"}
        type={["count", "number", "measurement"].includes(field.valueType) ? "number" : "text"}
        value={value}
      />
      {field.unit ? (
        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center pt-1 text-xs font-bold uppercase text-stone-400">
          {field.unit.replaceAll("_", " ")}
        </span>
      ) : null}
    </div>
  );
}

export function QuoteReviewModal({ busy, error, form, onClose, onSubmit, open }: Props) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [localError, setLocalError] = useState("");
  const scopes = form?.scopes ?? [];
  const visibleScopes = useMemo(
    () =>
      scopes.map((scope) => ({
        ...scope,
        primary: scope.fields.filter(shown),
        additional: scope.fields.filter((field) => field.valueType !== "photo" && !shown(field)),
      })),
    [scopes],
  );

  useEffect(() => {
    if (!open) return;
    setValues(
      Object.fromEntries(
        scopes.flatMap((scope) =>
          scope.fields.map((field) => [key(scope.projectType, field.field), initialValue(field)]),
        ),
      ),
    );
    setLocalError("");
  }, [open, scopes]);

  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [busy, onClose, open]);

  if (!open) return null;

  const submit = async () => {
    const missing = visibleScopes.flatMap((scope) =>
      scope.fields.filter(
        (field) =>
          field.required && values[key(scope.projectType, field.field)]?.trim().length === 0,
      ),
    );
    if (missing.length > 0) {
      setLocalError(`Enter ${missing[0]!.label.toLowerCase()} to calculate the quote.`);
      return;
    }
    const invalid = visibleScopes
      .flatMap((scope) =>
        scope.fields.map((field) => ({
          field,
          value: values[key(scope.projectType, field.field)] ?? "",
        })),
      )
      .find(
        ({ field, value }) =>
          value.trim().length > 0 &&
          ["count", "number", "measurement"].includes(field.valueType) &&
          (!Number.isFinite(Number(value)) ||
            (field.valueType === "measurement" && Number(value) <= 0)),
      );
    if (invalid) {
      setLocalError(`Enter a valid ${invalid.field.label.toLowerCase()}.`);
      return;
    }
    const facts = visibleScopes.flatMap((scope) =>
      scope.fields.flatMap((field) => {
        const value = values[key(scope.projectType, field.field)] ?? "";
        if (!value.trim()) return [];
        const parsed = inputValue(field, value);
        return [
          {
            projectType: scope.projectType,
            field: field.field,
            value: parsed,
            ...(field.unit ? { unit: field.unit } : {}),
          },
        ];
      }),
    );
    setLocalError("");
    try {
      await onSubmit(facts);
    } catch {
      // The parent owns the API error message so the modal can remain open for correction.
    }
  };

  return (
    <div
      aria-labelledby="quote-review-title"
      aria-modal="true"
      className="fixed inset-0 z-50 grid place-items-center bg-emerald-950/55 p-3 backdrop-blur-sm sm:p-6"
      role="dialog"
    >
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
        <header className="flex items-start justify-between gap-4 border-b border-stone-200 px-5 py-4 sm:px-7">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-emerald-700">
              Deterministic quote
            </p>
            <h2 className="mt-1 text-2xl font-black text-emerald-950" id="quote-review-title">
              Review quote details
            </h2>
            <p className="mt-1 text-sm text-stone-500">
              The project guide filled this in. Correct anything that does not look right.
            </p>
          </div>
          <button
            aria-label="Close quote review"
            className="rounded-xl p-2 text-stone-500 hover:bg-stone-100"
            disabled={busy}
            onClick={onClose}
            type="button"
          >
            <X size={20} />
          </button>
        </header>

        <div className="overflow-y-auto px-5 py-5 sm:px-7">
          {visibleScopes.map((scope) => (
            <section className="mb-7 last:mb-0" key={scope.workItemId}>
              <h3 className="text-lg font-black capitalize text-stone-900">{scope.label}</h3>
              <p className="mt-1 text-sm text-stone-500">
                Fields marked required affect whether this estimate can be calculated.
              </p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                {scope.primary.map((field) => {
                  const id = `quote-${scope.projectType}-${field.field}`;
                  return (
                    <label className="text-sm font-bold text-stone-700" htmlFor={id} key={id}>
                      <span className="flex items-center gap-2 capitalize">
                        {field.label.replaceAll("_", " ")}
                        {field.required ? <span className="text-red-600">*</span> : null}
                        {field.source !== "unknown" ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700">
                            <Check size={10} />
                            {field.source === "calculated"
                              ? "calculated"
                              : field.source === "contractor_default"
                                ? "standard"
                                : "filled"}
                          </span>
                        ) : null}
                      </span>
                      <QuoteFieldInput
                        field={field}
                        id={id}
                        onChange={(value) =>
                          setValues((current) => ({
                            ...current,
                            [key(scope.projectType, field.field)]: value,
                          }))
                        }
                        value={values[key(scope.projectType, field.field)] ?? ""}
                      />
                      {field.help ? (
                        <span className="mt-1 block text-xs font-normal text-stone-500">
                          {field.help}
                        </span>
                      ) : null}
                    </label>
                  );
                })}
              </div>
              {scope.additional.length > 0 ? (
                <details className="mt-5 rounded-xl border border-stone-200 bg-stone-50">
                  <summary className="cursor-pointer px-4 py-3 text-sm font-bold text-stone-700">
                    Add more detail ({scope.additional.length})
                  </summary>
                  <div className="grid gap-4 border-t border-stone-200 p-4 sm:grid-cols-2">
                    {scope.additional.map((field) => {
                      const id = `quote-${scope.projectType}-${field.field}`;
                      return (
                        <label
                          className="text-sm font-bold capitalize text-stone-700"
                          htmlFor={id}
                          key={id}
                        >
                          {field.label.replaceAll("_", " ")}
                          <QuoteFieldInput
                            field={field}
                            id={id}
                            onChange={(value) =>
                              setValues((current) => ({
                                ...current,
                                [key(scope.projectType, field.field)]: value,
                              }))
                            }
                            value={values[key(scope.projectType, field.field)] ?? ""}
                          />
                        </label>
                      );
                    })}
                  </div>
                </details>
              ) : null}
            </section>
          ))}
          {visibleScopes.length === 0 ? (
            <div className="rounded-2xl bg-stone-50 p-6 text-center">
              <Calculator className="mx-auto text-emerald-700" />
              <p className="mt-3 font-bold">No additional values are required.</p>
              <p className="mt-1 text-sm text-stone-500">
                This contractor’s published price can be calculated from the current project scope.
              </p>
            </div>
          ) : null}
          {localError || error ? (
            <p className="mt-4 rounded-xl bg-red-50 p-3 text-sm font-semibold text-red-700">
              {localError || error}
            </p>
          ) : null}
        </div>

        <footer className="flex flex-col-reverse gap-2 border-t border-stone-200 bg-stone-50 px-5 py-4 sm:flex-row sm:justify-end sm:px-7">
          <SecondaryButton disabled={busy} onClick={onClose} type="button">
            Keep editing later
          </SecondaryButton>
          <Button disabled={busy} onClick={() => void submit()} type="button">
            {busy ? "Calculating quote…" : "Confirm details & calculate"}
          </Button>
        </footer>
      </div>
    </div>
  );
}
